import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { WidgetInsightsSlot } from '../assistant/WidgetInsightCallout';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import {
  formatMonthKeyLabel,
  shiftMonthKey,
} from '../../hooks/useSpendBreakdown';
import { useSpendCategoryTrends } from '../../hooks/useSpendCategoryTrends';
import {
  useSpendTrendInsight,
  type TrendDriver,
  type TrendMom,
  type TrendPreviewTx,
} from '../../hooks/useSpendTrendInsight';
import { useWidgetInsights } from '../../hooks/useWidgetInsights';
import type { AnalyticsTrendPoint } from '../../types/analytics';
import { formatCurrency, formatK } from '../charts/d3/format';
import { ErrorRetry } from '../ui/ErrorRetry';
import { GlassCard } from '../ui/GlassCard';
import { RefreshButton } from '../ui/RefreshButton';
import type { SpendCategoryModalTarget } from './spendCategoryModal';
import { SpendCategoryChipRail } from './SpendCategoryChipRail';

export type SpendTrendCategory = {
  id: string;
  name: string;
  color: string;
};

type Props = {
  categories: SpendTrendCategory[];
  /** When a month is focused, sync the Spend month navigator. */
  onFocusMonth?: (monthKey: string) => void;
  /** Open category transactions modal for a focused month. */
  onOpenCategoryMonth?: (target: SpendCategoryModalTarget) => void;
  /** Browse all spend transactions for a focused month. */
  onOpenAllMonth?: (periodKey: string) => void;
  /** Open merchant transactions for a focused month. */
  onOpenMerchant?: (
    categorySlug: string,
    merchantNormalized: string,
    periodKey: string,
  ) => void;
};

const CHART_HEIGHT = 132;

function shortMonthLabel(periodKey: string): string {
  const [year, month] = periodKey.split('-').map(Number);
  if (!year || !month) return periodKey;
  const date = new Date(Date.UTC(year, month - 1, 1));
  return date.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' });
}

function amountOf(points: AnalyticsTrendPoint[], key: string): number {
  return Math.abs(points.find((p) => p.periodKey === key)?.totalAmount ?? 0);
}

function defaultFocusKey(points: AnalyticsTrendPoint[]): string | null {
  if (points.length === 0) return null;
  for (let i = points.length - 1; i >= 0; i--) {
    const point = points[i]!;
    if (Math.abs(point.totalAmount) > 0 || point.transactionCount > 0) {
      return point.periodKey;
    }
  }
  return points[points.length - 1]?.periodKey ?? null;
}

function formatDelta(current: number, previous: number): {
  text: string;
  tone: 'up' | 'down' | 'flat';
} {
  const delta = current - previous;
  if (Math.abs(delta) < 0.5) return { text: 'About the same', tone: 'flat' };
  const tone = delta > 0 ? 'up' : 'down';
  const money = `${delta > 0 ? '+' : '−'}${formatCurrency(Math.abs(delta))}`;
  if (previous < 5) return { text: money, tone };
  const pct = Math.round((delta / previous) * 100);
  if (Math.abs(pct) >= 150) return { text: money, tone };
  if (pct === 0) return { text: money, tone };
  return {
    text: `${money} (${pct > 0 ? '+' : ''}${pct}%)`,
    tone,
  };
}

function formatShortDate(iso: string): string {
  const date = new Date(iso.includes('T') ? iso : `${iso}T12:00:00`);
  if (Number.isNaN(date.getTime())) return iso.slice(0, 10);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Category chips + monthly bar chart. Tap a month for MoM insight + drill-down.
 */
export function SpendCategoryTrends({
  categories,
  onFocusMonth,
  onOpenCategoryMonth,
  onOpenAllMonth,
  onOpenMerchant,
}: Props) {
  const [categoryId, setCategoryId] = useState<string>('all');
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const [userPickedFocus, setUserPickedFocus] = useState(false);

  const activeCategory = categories.find((c) => c.id === categoryId);
  const barColor = activeCategory?.color ?? colors.accent;

  const { points, loading, error, isLive, refresh } = useSpendCategoryTrends({
    categoryId,
    limit: 6,
    enabled: true,
  });

  const maxAmount = useMemo(
    () => Math.max(...points.map((p) => Math.abs(p.totalAmount)), 1),
    [points],
  );

  // Default focus when trend points arrive (category switch clears focus until then).
  useEffect(() => {
    if (userPickedFocus && focusedKey && points.some((p) => p.periodKey === focusedKey)) {
      return;
    }
    const next = defaultFocusKey(points);
    setFocusedKey(next);
    if (next) onFocusMonth?.(next);
    // Intentionally omit callbacks / focus flags to avoid re-sync loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points]);

  const { insights: aiInsights, loading: aiLoading } = useWidgetInsights(
    'spend_trends',
    { enabled: isLive },
  );
  const hasAiInsight = aiInsights.length > 0;
  // Wait for AI fetch before showing local MoM so we don't flash the fallback.
  const showLocalTrendFallback = !aiLoading && !hasAiInsight;

  const {
    mom,
    drivers,
    previewTxs,
    loading: insightLoading,
    error: insightError,
  } = useSpendTrendInsight({
    focusedMonthKey: focusedKey,
    categoryId,
    points,
    // Local MoM panel is the non-AI fallback when no precomputed card matches.
    enabled: isLive && !!focusedKey && showLocalTrendFallback,
  });

  if (!isLive) return null;

  const hasAnySpend = points.some((p) => Math.abs(p.totalAmount) > 0);
  const categoryLabel = activeCategory?.name ?? 'All spending';
  const previousKey = focusedKey ? shiftMonthKey(focusedKey, -1) : null;

  const handleFocusMonth = (key: string) => {
    setFocusedKey(key);
    setUserPickedFocus(true);
    onFocusMonth?.(key);
  };

  const handleClearFocus = () => {
    const next = defaultFocusKey(points);
    setFocusedKey(next);
    setUserPickedFocus(false);
    if (next) onFocusMonth?.(next);
  };

  const handleViewAll = () => {
    if (!focusedKey) return;
    if (categoryId === 'all') {
      onOpenAllMonth?.(focusedKey);
      return;
    }
    onOpenCategoryMonth?.({
      categorySlug: categoryId,
      categoryName: categoryLabel,
      amount: amountOf(points, focusedKey),
      period: 'month',
      periodKey: focusedKey,
    });
  };

  const handleDriverPress = (driver: TrendDriver) => {
    if (!focusedKey) return;
    if (driver.kind === 'category' && driver.categorySlug) {
      onOpenCategoryMonth?.({
        categorySlug: driver.categorySlug,
        categoryName: driver.label,
        amount: driver.currentAmount,
        period: 'month',
        periodKey: focusedKey,
      });
      return;
    }
    if (driver.kind === 'merchant' && driver.merchantNormalized && driver.categorySlug) {
      onOpenMerchant?.(driver.categorySlug, driver.merchantNormalized, focusedKey);
    }
  };

  return (
    <GlassCard style={styles.card}>
      <View style={styles.header}>
        <View style={styles.headerText}>
          <Text style={styles.title}>Trends</Text>
          <Text style={styles.hint}>Tap a month to see what changed</Text>
        </View>
        <View style={styles.headerActions}>
          <RefreshButton onPress={refresh} refreshing={loading} compact />
          <Text style={styles.categoryBadge} numberOfLines={1}>
            {categoryLabel}
          </Text>
        </View>
      </View>

      <SpendCategoryChipRail
        categories={categories}
        selectedId={categoryId}
        onSelect={(id) => {
          setCategoryId(id);
          setUserPickedFocus(false);
          setFocusedKey(null);
        }}
      />

      {error ? (
        <ErrorRetry message={error} onRetry={refresh} refreshing={loading} compact />
      ) : null}

      {loading && !hasAnySpend ? (
        <ActivityIndicator color={colors.accent} style={styles.loader} />
      ) : !hasAnySpend ? (
        <Text style={styles.empty}>No monthly history for {categoryLabel} yet.</Text>
      ) : (
        <View style={styles.chartBlock}>
          <Text style={styles.axisCaption}>Spend by month · {categoryLabel}</Text>
          <View style={styles.chartRow}>
            <View style={styles.yAxis}>
              <Text style={styles.yTick}>{formatK(maxAmount)}</Text>
              <Text style={styles.yTick}>{formatK(maxAmount / 2)}</Text>
              <Text style={styles.yTick}>$0</Text>
            </View>
            <View style={styles.chart}>
              {points.map((point) => {
                const amount = Math.abs(point.totalAmount);
                const height = Math.max((amount / maxAmount) * CHART_HEIGHT, amount > 0 ? 6 : 2);
                const focused = focusedKey === point.periodKey;
                const isCompare = previousKey === point.periodKey && !focused;
                const isMuted = focusedKey != null && !focused && !isCompare;

                return (
                  <Pressable
                    key={point.periodKey}
                    accessibilityRole="button"
                    accessibilityLabel={`${shortMonthLabel(point.periodKey)} ${formatCurrency(amount)}`}
                    accessibilityState={{ selected: focused }}
                    onPress={() => handleFocusMonth(point.periodKey)}
                    style={({ pressed }) => [styles.barCol, pressed && styles.pressed]}
                  >
                    <Text style={styles.barAmount} numberOfLines={1}>
                      {amount > 0 ? formatK(amount) : '—'}
                    </Text>
                    <View style={styles.barTrack}>
                      <View style={styles.gridLineTop} />
                      <View style={styles.gridLineMid} />
                      <View
                        style={[
                          styles.barFill,
                          {
                            height,
                            backgroundColor: barColor,
                            opacity: isMuted ? 0.28 : focused ? 1 : isCompare ? 0.55 : 0.72,
                          },
                          focused && styles.barSelected,
                        ]}
                      />
                    </View>
                    <Text style={[styles.barLabel, focused && styles.barLabelSelected]}>
                      {shortMonthLabel(point.periodKey)}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
          <Text style={styles.xAxisCaption}>Month</Text>
        </View>
      )}

      {hasAiInsight ? (
        <WidgetInsightsSlot widget="spend_trends" style={styles.aiCallout} />
      ) : showLocalTrendFallback && focusedKey && mom ? (
        <InsightPanel
          categoryLabel={categoryLabel}
          mom={mom}
          drivers={drivers}
          previewTxs={previewTxs}
          loading={insightLoading}
          error={insightError}
          showClear={userPickedFocus}
          onClear={handleClearFocus}
          onViewAll={handleViewAll}
          onDriverPress={handleDriverPress}
        />
      ) : null}

    </GlassCard>
  );
}

function InsightPanel({
  categoryLabel,
  mom,
  drivers,
  previewTxs,
  loading,
  error,
  showClear,
  onClear,
  onViewAll,
  onDriverPress,
}: {
  categoryLabel: string;
  mom: TrendMom;
  drivers: TrendDriver[];
  previewTxs: TrendPreviewTx[];
  loading: boolean;
  error: string | null;
  showClear: boolean;
  onClear: () => void;
  onViewAll: () => void;
  onDriverPress: (driver: TrendDriver) => void;
}) {
  const delta = formatDelta(mom.currentAmount, mom.previousAmount);
  const countDelta =
    mom.deltaCount === 0
      ? 'same # of txs'
      : `${mom.deltaCount > 0 ? '+' : ''}${mom.deltaCount} txs`;

  return (
    <View style={styles.insight}>
      <View style={styles.insightHeader}>
        <Text style={styles.insightTitle}>
          {formatMonthKeyLabel(mom.focusedKey)} vs {shortMonthLabel(mom.previousKey)}
        </Text>
        {showClear ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Reset to latest month"
            hitSlop={8}
            onPress={onClear}
          >
            <Text style={styles.clearText}>Reset</Text>
          </Pressable>
        ) : null}
      </View>

      <Text style={styles.momAmount}>{formatCurrency(mom.currentAmount)}</Text>
      <Text
        style={[
          styles.deltaText,
          delta.tone === 'up' && styles.deltaUp,
          delta.tone === 'down' && styles.deltaDown,
        ]}
      >
        {categoryLabel}: {delta.text} vs {shortMonthLabel(mom.previousKey)}
      </Text>
      <Text style={styles.compareFoot}>
        {mom.currentCount} transactions · {countDelta}
      </Text>

      {error ? <ErrorRetry message={error} compact /> : null}

      {loading && drivers.length === 0 && previewTxs.length === 0 ? (
        <ActivityIndicator color={colors.accent} style={styles.insightLoader} />
      ) : (
        <>
          {drivers.length > 0 ? (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>What changed</Text>
              {drivers.map((driver) => {
                const driverDelta = formatDelta(driver.currentAmount, driver.previousAmount);
                return (
                  <Pressable
                    key={driver.id}
                    accessibilityRole="button"
                    accessibilityLabel={`${driver.label} ${driverDelta.text}`}
                    onPress={() => onDriverPress(driver)}
                    style={({ pressed }) => [styles.driverRow, pressed && styles.pressed]}
                  >
                    <View style={styles.driverText}>
                      <Text style={styles.driverLabel} numberOfLines={1}>
                        {driver.label}
                      </Text>
                      <Text style={styles.driverSub} numberOfLines={1}>
                        {formatCurrency(driver.currentAmount)}
                      </Text>
                    </View>
                    <Text
                      style={[
                        styles.driverDelta,
                        driverDelta.tone === 'up' && styles.deltaUp,
                        driverDelta.tone === 'down' && styles.deltaDown,
                      ]}
                    >
                      {driverDelta.text}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          ) : !loading ? (
            <Text style={styles.emptyDrivers}>No notable shifts vs last month.</Text>
          ) : null}

          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Transactions</Text>
              {previewTxs.length > 0 || mom.currentCount > 0 ? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="View all transactions"
                  hitSlop={8}
                  onPress={onViewAll}
                >
                  <Text style={styles.clearText}>View all</Text>
                </Pressable>
              ) : null}
            </View>
            {previewTxs.length === 0 && !loading ? (
              <Text style={styles.emptyDrivers}>No transactions this month.</Text>
            ) : (
              previewTxs.map((tx) => (
                <Pressable
                  key={tx.id}
                  accessibilityRole="button"
                  accessibilityLabel={`${tx.merchant} ${formatCurrency(tx.amount)}`}
                  onPress={onViewAll}
                  style={({ pressed }) => [styles.txRow, pressed && styles.pressed]}
                >
                  <View style={styles.txText}>
                    <Text style={styles.txMerchant} numberOfLines={1}>
                      {tx.merchant}
                    </Text>
                    <Text style={styles.txDate}>{formatShortDate(tx.date)}</Text>
                  </View>
                  <Text
                    style={[
                      styles.txAmount,
                      tx.direction === 'credit' && styles.txCredit,
                    ]}
                  >
                    {tx.direction === 'credit' ? '+' : '−'}
                    {formatCurrency(tx.amount)}
                  </Text>
                </Pressable>
              ))
            )}
          </View>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  headerText: {
    flex: 1,
    gap: 4,
    minWidth: 0,
  },
  title: {
    ...typography.headline,
    color: colors.text,
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  categoryBadge: {
    ...typography.caption,
    color: colors.accent,
    fontWeight: '700',
    maxWidth: 140,
    textAlign: 'right',
  },
  loader: {
    marginVertical: spacing.md,
  },
  insightLoader: {
    marginVertical: spacing.sm,
  },
  empty: {
    ...typography.caption,
    color: colors.textMuted,
    paddingVertical: spacing.md,
  },
  errorText: {
    ...typography.caption,
    color: colors.warning,
  },
  chartBlock: {
    gap: spacing.xs,
  },
  axisCaption: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
  },
  xAxisCaption: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
    textAlign: 'center',
    marginTop: 2,
  },
  chartRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: spacing.xs,
  },
  yAxis: {
    width: 40,
    height: CHART_HEIGHT + 36,
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    paddingBottom: 18,
    paddingTop: 18,
  },
  yTick: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 10,
    fontVariant: ['tabular-nums'],
  },
  chart: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 6,
    minHeight: CHART_HEIGHT + 52,
    paddingTop: spacing.sm,
  },
  barCol: {
    flex: 1,
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },
  barAmount: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
    height: 16,
  },
  barTrack: {
    height: CHART_HEIGHT,
    width: '100%',
    maxWidth: 48,
    justifyContent: 'flex-end',
    alignItems: 'center',
    position: 'relative',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
  },
  gridLineTop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: 'rgba(255,255,255,0.08)',
  },
  gridLineMid: {
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: 'rgba(255,255,255,0.06)',
  },
  barFill: {
    width: '72%',
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    borderBottomLeftRadius: 2,
    borderBottomRightRadius: 2,
  },
  barSelected: {
    shadowColor: colors.accent,
    shadowOpacity: 0.35,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 0 },
  },
  barLabel: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
    fontWeight: '600',
  },
  barLabelSelected: {
    color: colors.text,
  },
  aiCallout: {
    marginTop: spacing.xs,
    paddingTop: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.glassBorder,
  },
  insight: {
    marginTop: spacing.xs,
    paddingTop: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.glassBorder,
    gap: spacing.sm,
  },
  insightHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  insightTitle: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '700',
    flex: 1,
  },
  clearText: {
    ...typography.caption,
    color: colors.accent,
    fontWeight: '700',
  },
  momAmount: {
    ...typography.headline,
    color: colors.text,
    fontSize: 22,
    fontVariant: ['tabular-nums'],
  },
  deltaText: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '600',
  },
  deltaUp: {
    color: colors.danger,
  },
  deltaDown: {
    color: colors.accent,
  },
  compareFoot: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
  },
  section: {
    gap: 6,
    marginTop: spacing.xs,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  sectionTitle: {
    ...typography.caption,
    color: colors.textMuted,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontSize: 11,
  },
  emptyDrivers: {
    ...typography.caption,
    color: colors.textMuted,
  },
  driverRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingVertical: 8,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.md,
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
  },
  driverText: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  driverLabel: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '600',
  },
  driverSub: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
    fontVariant: ['tabular-nums'],
  },
  driverDelta: {
    ...typography.caption,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
    color: colors.text,
  },
  txRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingVertical: 6,
  },
  txText: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  txMerchant: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '600',
  },
  txDate: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
  },
  txAmount: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
  },
  txCredit: {
    color: colors.accent,
  },
  pressed: {
    opacity: 0.8,
  },
});
